import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { browserDefaultMic, dedupeAudioInputs, resolveMicChoice, type MicDevice } from "./micDevices";
import { currentMicPlatform, type MicPlatform } from "./micPlatform";
import { loadMicChoice, saveMicChoice, type SavedMicChoice } from "./micPreference";
import { openMicStream, resolveStartAudioConstraints } from "./micStart";
import {
    classifyGetUserMediaError,
    idleMicState,
    MIC_BLOCKING_STATES,
    type MicPermission,
    type MicStateId,
} from "./micStatus";

export interface MicContextValue {
    state: MicStateId;
    errorMessage: string | null;
    inputs: MicDevice[];
    selected: MicDevice | null; // null: the browser's own default, unnamed (Firefox, Safari)
    platform: MicPlatform;
    busy: boolean;
    listOpen: boolean;
    probeStream: MediaStream | null;
    refresh: () => Promise<MicStateId>;
    allow: () => Promise<MicStateId>;
    recheck: () => Promise<MicStateId>;
    checkBeforeRecord: () => Promise<{ ok: true } | { ok: false; state: MicStateId }>;
    reportStartFailure: (err: unknown) => MicStateId;
    openList: () => Promise<void>;
    closeList: () => void;
    choose: (d: MicDevice) => Promise<void>;
}

// Used outside a MicProvider (isolated tests, previews): behaves like a healthy mic and never opens it.
const INERT: MicContextValue = {
    state: "R10",
    errorMessage: null,
    inputs: [],
    selected: null,
    platform: { browser: "other", os: "other" },
    busy: false,
    listOpen: false,
    probeStream: null,
    refresh: async () => "R10",
    allow: async () => "R10",
    recheck: async () => "R10",
    checkBeforeRecord: async () => ({ ok: true }),
    reportStartFailure: () => "R10",
    openList: async () => {},
    closeList: () => {},
    choose: async () => {},
};

const MicContext = createContext<MicContextValue>(INERT);

export const useMic = (): MicContextValue => useContext(MicContext);

const stopStream = (stream: MediaStream | null | undefined) => {
    stream?.getTracks().forEach((t) => t.stop());
};

const crumb = (message: string, data?: Record<string, unknown>) => {
    Sentry.addBreadcrumb({ category: "recording", level: "info", message, data });
};

// A hung enumerateDevices counts as a failure after this long.
const REFRESH_ENUMERATE_TIMEOUT_MS = 1000;

// Resolves null when enumerateDevices rejects, is missing, or hangs.
const enumerateWithTimeout = async (): Promise<MediaDeviceInfo[] | null> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        const enumerate = navigator.mediaDevices?.enumerateDevices;
        if (!enumerate) return null;
        return await Promise.race([
            navigator.mediaDevices.enumerateDevices(),
            new Promise<null>((resolve) => {
                timer = setTimeout(() => resolve(null), REFRESH_ENUMERATE_TIMEOUT_MS);
            }),
        ]);
    } catch {
        return null;
    } finally {
        if (timer) clearTimeout(timer);
    }
};

// The saved mic if plugged in, else the browser default when the browser names it (Chrome), else null:
// a start then uses the browser's own default, which Firefox and Safari do not identify.
const pickSelected = (inputs: MicDevice[], choice: SavedMicChoice | null): MicDevice | null =>
    resolveMicChoice(choice, inputs) ?? browserDefaultMic(inputs);

export const MicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const platform = useMemo(() => currentMicPlatform(), []);
    const [state, setState] = useState<MicStateId>("R10");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [inputs, setInputs] = useState<MicDevice[]>([]);
    const [choice, setChoice] = useState<SavedMicChoice | null>(() => loadMicChoice());
    const [busy, setBusy] = useState(false);
    const [listOpen, setListOpen] = useState(false);
    const [probeStream, setProbeStream] = useState<MediaStream | null>(null);

    const mountedRef = useRef(true);
    const refreshSeqRef = useRef(0);
    const probeSeqRef = useRef(0);
    const stateRef = useRef<MicStateId>("R10");
    const permissionRef = useRef<MicPermission>("unknown");
    const inputsRef = useRef<MicDevice[]>([]);
    const enumeratedRef = useRef(false); // an enumeration has succeeded at least once
    const choiceRef = useRef<SavedMicChoice | null>(choice);
    const listOpenRef = useRef(false);
    const probeRef = useRef<MediaStream | null>(null);
    const statusRef = useRef<PermissionStatus | null>(null);
    const refreshRef = useRef<() => Promise<MicStateId>>(async () => "R10");

    const commitState = useCallback((next: MicStateId, message: string | null = null) => {
        stateRef.current = next;
        if (!mountedRef.current) return;
        setState(next);
        setErrorMessage(message);
    }, []);

    const stopProbe = useCallback(() => {
        probeSeqRef.current += 1;
        stopStream(probeRef.current);
        probeRef.current = null;
        if (mountedRef.current) setProbeStream(null);
    }, []);

    const readPermission = useCallback(async (): Promise<MicPermission> => {
        try {
            const query = navigator.permissions?.query;
            if (!query) return "unknown";
            const status = await navigator.permissions.query({ name: "microphone" as PermissionName });
            if (statusRef.current !== status) {
                if (statusRef.current) statusRef.current.onchange = null;
                statusRef.current = status;
                status.onchange = () => {
                    void refreshRef.current();
                };
            }
            const s = status.state as string;
            return s === "granted" || s === "denied" || s === "prompt" ? s : "unknown";
        } catch {
            return "unknown";
        }
    }, []);

    // Reads permission and device list only. Never opens the mic.
    const refresh = useCallback(async (): Promise<MicStateId> => {
        const seq = ++refreshSeqRef.current;
        const permission = await readPermission();
        const devices = await enumerateWithTimeout();
        // A newer refresh or an unmount supersedes this one.
        if (seq !== refreshSeqRef.current || !mountedRef.current) return stateRef.current;
        const nextChoice = loadMicChoice();
        permissionRef.current = permission;
        choiceRef.current = nextChoice;
        setChoice(nextChoice);
        // A failed enumeration is not "no microphone": keep the last good list, or with none,
        // derive the state from permission alone (never R16).
        let known: MicDevice[] | null = enumeratedRef.current ? inputsRef.current : null;
        if (devices) {
            known = dedupeAudioInputs(devices);
            enumeratedRef.current = true;
            inputsRef.current = known;
            setInputs(known);
        }
        const next = idleMicState({ permission, inputs: known, platform });
        commitState(next);
        return next;
    }, [commitState, platform, readPermission]);
    refreshRef.current = refresh;

    const reportStartFailure = useCallback(
        (err: unknown): MicStateId => {
            // A refresh already in flight must not overwrite this failure with an idle state.
            refreshSeqRef.current += 1;
            const next = classifyGetUserMediaError(err, permissionRef.current, platform);
            const name = err && typeof err === "object" ? (err as { name?: unknown }).name : undefined;
            const message = err && typeof err === "object" ? (err as { message?: unknown }).message : undefined;
            crumb("mic start failure", { state: next, errorName: typeof name === "string" ? name : undefined });
            listOpenRef.current = false;
            stopProbe();
            if (mountedRef.current) setListOpen(false);
            commitState(next, next === "error" ? (typeof message === "string" ? message : null) : null);
            return next;
        },
        [commitState, platform, stopProbe],
    );

    const probe = useCallback(
        // null probes the browser's own default mic, the one a start with no saved choice opens.
        async (device: MicDevice | null) => {
            stopProbe();
            const seq = probeSeqRef.current;
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: device ? { deviceId: { ideal: device.deviceId } } : true,
                });
                if (seq !== probeSeqRef.current || !mountedRef.current || !listOpenRef.current) {
                    stopStream(stream);
                    return;
                }
                probeRef.current = stream;
                setProbeStream(stream);
            } catch (err) {
                if (seq === probeSeqRef.current && mountedRef.current) reportStartFailure(err);
            }
        },
        [reportStartFailure, stopProbe],
    );

    // allow and recheck are the same action: open the mic with the saved-choice constraints a recording would use (not any caller-supplied audioTrackConstraints), then release it.
    const openAndRelease = useCallback(async (): Promise<MicStateId> => {
        if (mountedRef.current) setBusy(true);
        try {
            const audio = await resolveStartAudioConstraints();
            const stream = await openMicStream(audio, true);
            stopStream(stream);
        } catch (err) {
            return reportStartFailure(err);
        } finally {
            if (mountedRef.current) setBusy(false);
        }
        return refresh();
    }, [refresh, reportStartFailure]);

    const checkBeforeRecord = useCallback(async () => {
        // The record press opens the mic itself: release the list's probe first so it cannot hold the device.
        listOpenRef.current = false;
        stopProbe();
        if (mountedRef.current) setListOpen(false);
        const next = await refresh();
        return MIC_BLOCKING_STATES.has(next) ? { ok: false as const, state: next } : { ok: true as const };
    }, [refresh, stopProbe]);

    const openList = useCallback(async () => {
        listOpenRef.current = true;
        setListOpen(true);
        await refresh();
        if (!listOpenRef.current || !mountedRef.current) return;
        await probe(pickSelected(inputsRef.current, choiceRef.current));
    }, [probe, refresh]);

    const closeList = useCallback(() => {
        listOpenRef.current = false;
        stopProbe();
        if (mountedRef.current) setListOpen(false);
    }, [stopProbe]);

    const choose = useCallback(
        async (d: MicDevice) => {
            const next = { deviceId: d.deviceId, label: d.label };
            saveMicChoice(next);
            choiceRef.current = next;
            if (mountedRef.current) setChoice(next);
            crumb("mic chosen", { builtIn: d.builtIn });
            if (listOpenRef.current) await probe(d);
        },
        [probe],
    );

    useEffect(() => {
        mountedRef.current = true;
        const devices = navigator.mediaDevices;
        const onDeviceChange = () => {
            void refreshRef.current();
        };
        devices?.addEventListener?.("devicechange", onDeviceChange);
        void refreshRef.current();
        return () => {
            mountedRef.current = false;
            devices?.removeEventListener?.("devicechange", onDeviceChange);
            if (statusRef.current) statusRef.current.onchange = null;
            statusRef.current = null;
            stopStream(probeRef.current);
            probeRef.current = null;
        };
    }, []);

    const selected = useMemo(() => pickSelected(inputs, choice), [inputs, choice]);

    const value = useMemo<MicContextValue>(
        () => ({
            state,
            errorMessage,
            inputs,
            selected,
            platform,
            busy,
            listOpen,
            probeStream,
            refresh,
            allow: openAndRelease,
            recheck: openAndRelease,
            checkBeforeRecord,
            reportStartFailure,
            openList,
            closeList,
            choose,
        }),
        [
            state,
            errorMessage,
            inputs,
            selected,
            platform,
            busy,
            listOpen,
            probeStream,
            refresh,
            openAndRelease,
            checkBeforeRecord,
            reportStartFailure,
            openList,
            closeList,
            choose,
        ],
    );

    return <MicContext.Provider value={value}>{children}</MicContext.Provider>;
};
